import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  ControlButton,
  Panel,
  MarkerType,
  ReactFlowProvider,
  useReactFlow,
  addEdge,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type Node,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Tooltip,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LayoutDirectionContext, type LayoutDirection } from './PrimitiveNode';
import RuleNode, { RULE_NODE_RF_TYPE, RULE_NODE_COLORS, type RuleNodeData } from './RuleNode';
import WorkflowEdge, { type WorkflowEdgeData } from './WorkflowEdge';
import RecipientPicker from './RecipientPicker';
import { hasValidPosition, computeFallbackPositions } from './ruleFlowLayout';
import { isNodeIncomplete, nodeRecommendationText } from './ruleFlowValidation';
import {
  serializeRuleFlow,
  confirmRuleFlow,
  updateNodeDescription,
  RuleFlowConfirmConflictError,
  RuleFlowNodeNotFoundError,
  RuleFlowDescriptionNotFoundError,
  type RuleFlow,
  type RuleFlowGraphNode,
  type RuleFlowGraphEdge,
  type RuleFlowRecipient,
} from '../../../services/channelRuleFlowsService';
import { saveChannelBusinessRules } from '../../../services/businessRulesService';
import type { FlowRun } from '../../../services/channelDashboardService';

const nodeTypes = { [RULE_NODE_RF_TYPE]: RuleNode };
const WORKFLOW_EDGE_RF_TYPE = 'workflowEdge';
const edgeTypes = { [WORKFLOW_EDGE_RF_TYPE]: WorkflowEdge };
const defaultEdgeOptions = { markerEnd: { type: MarkerType.ArrowClosed, color: '#9e9e9e', width: 18, height: 18 } };
// FIX 10 (frontend handover) — a background chip behind each branch-condition label so it stays
// legible where an edge crosses near or behind a node. Independent copy of the same idea
// WorkflowCanvasTab.tsx already uses for its own edge labels, not a shared import.
const EDGE_LABEL_STYLE = { fontSize: 11, fontWeight: 700, fill: '#37474f' };
const EDGE_LABEL_BG_STYLE = { fill: '#ffffff', fillOpacity: 0.92 };
const EDGE_LABEL_BG_PADDING: [number, number] = [6, 3];

// Same wording used in WorkflowCanvasTab.tsx's own lock button — kept as separate copies (not a
// shared import) since these two canvases are deliberately independent everywhere else; update
// both if this text ever changes.
const INTERACTIVE_TOGGLE_TOOLTIP =
  'Interactive (On): Fully editable — add, drag, and configure nodes or connections.\n' +
  'Non-Interactive (Off): Canvas locked — freezes elements to prevent accidental edits while viewing, presenting, or testing.';

// FIX 9b/11 (frontend handover) — a staged rename/reword edit, accumulated locally with zero
// network calls until FIX 11's Save button fires them, one at a time in order (the "sequential"
// option the doc recommends starting with — needs nothing from the backend beyond what FIX 5-7
// already spec). Deliberately separate from recipient edits (FIX 1-3), which keep auto-saving
// immediately exactly as before.
//
// Insert-step (FIX 9a) and delete-node (FIX 13-FE) were both originally staged the same way, but
// per explicit request both now save immediately instead (see handleSubmitInsert and
// handleDeleteNode) — each is a single, self-contained action confirmed by its own distinct
// click (a dialog's "Insert" button; an edge's own delete icon), unlike a rename typed directly
// on the canvas with no separate confirm step of its own. Neither ever enters this list.
interface PendingNodeEditDiff {
  kind: 'edited_node';
  nodeId: string;
  /** For the pending-changes summary list only — never sent to the backend. */
  nodeLabel: string;
  label?: string;
}
type PendingDiff = PendingNodeEditDiff;

/**
 * PR #635 review (C1, Critical): every real node arrives with position: null — no BE/ORCH
 * producer ever populates a real {x, y} (confirmed in shay-email-ml#603). React Flow's
 * Node.position is required internally, so this computes a fallback layout for any node
 * missing one instead of trusting the field is always usable.
 */
// Exported for direct unit testing (branchCount / label wiring, FIX 10) — the ReactFlow test
// mock elsewhere in this feature discards nodes/edges data it doesn't need for its own
// rendering, so those specific transforms are easier to verify directly than through it.
export function docToRfNodes(
  nodes: RuleFlowGraphNode[],
  edges: RuleFlowGraphEdge[],
  // Backend never sends a real position (it's a canvas-only hint, never interpreted — see the
  // comment above), so a recipient edit's re-sync recomputes this fallback layout from scratch
  // every time. Threading the current toggle through keeps that re-sync consistent with
  // whatever direction Auto-layout was last set to, instead of silently reverting to vertical.
  direction: 'vertical' | 'horizontal' = 'vertical'
): Node<RuleNodeData>[] {
  const needsFallback = nodes.some((n) => !hasValidPosition(n.position));
  const fallbackPositions = needsFallback
    ? computeFallbackPositions(
        nodes.map((n) => n.id),
        edges.map((e) => ({ source: e.source, target: e.target })),
        direction
      )
    : {};

  // FIX 10 (frontend handover) — a CONDITION with 2 outgoing edges gets a distinct branch
  // visual; derived here from the flow's own edges array (a CONDITION with exactly 1 outgoing
  // edge, today's only real case, computes to 1 and renders exactly as it always has).
  const outgoingCountBySource: Record<string, number> = {};
  edges.forEach((e) => {
    outgoingCountBySource[e.source] = (outgoingCountBySource[e.source] ?? 0) + 1;
  });

  return nodes.map((n) => ({
    id: n.id,
    type: RULE_NODE_RF_TYPE,
    position: hasValidPosition(n.position) ? n.position : fallbackPositions[n.id] ?? { x: 0, y: 0 },
    // RuleFlowGraphNode's own fields (label/recipients/meta) are flat, not nested under a
    // `.data` sub-object (see channelRuleFlowsService.ts) — this `data` is React Flow's own
    // convention for the Node this function builds, unrelated to the wire shape.
    data: {
      nodeType: n.type,
      label: n.label ?? '',
      recipients: n.recipients,
      // PRISM7_CHANNEL_RULES_NODE_DESCRIPTIONS_TDD §3.1/§6 — persisted annotation, straight from
      // the server; absent on historical rows parsed before this feature.
      description: n.description,
      descriptionSource: n.description_source,
      // Bug found live — computed ONCE here from the server's own node (n), never from the
      // live/edited recipients array: FIX 2's "empty array = no hand-off concept, hide the
      // picker" rule is correct for a node that never had recipients from parse, but the
      // moment an admin cleared out a placeholder recipient intending to pick a real one,
      // the LIVE array also went empty — the same rule then hid the RecipientPicker entirely,
      // with no way left to pick a replacement, and auto-save persisted it that way. This
      // flag is set once from server truth at load/re-parse time and never mutated by
      // handleRecipientsChange's optimistic writes, so clearing every recipient can't make the
      // control it's needed for disappear.
      hasRecipientConcept: n.type === 'ACTION' && (n.recipients?.length ?? 0) > 0,
      // ADR §3 — canvas-level "incomplete flow" hard-block: an approval-requiring ACTION with no
      // resolved recipient. Computed here (not inside RuleNode) so the presentational node
      // component stays free of this business rule's details.
      isIncomplete: isNodeIncomplete(n),
      // Fix 14/Item 7 — combines the FE-derived missing-recipient text with the backend's own
      // node.recommendations[] (parse); RuleNode.tsx renders whatever ends up in this list
      // without caring which source it came from.
      recommendationTexts: [
        ...(nodeRecommendationText(n) ? [nodeRecommendationText(n) as string] : []),
        ...(n.recommendations ?? []),
      ],
      branchCount: outgoingCountBySource[n.id] ?? 0,
    },
  }));
}

export function docToRfEdges(edges: RuleFlowGraphEdge[]): Edge<WorkflowEdgeData>[] {
  return edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    type: WORKFLOW_EDGE_RF_TYPE,
    // FIX 10 — a CONDITION's branch edges each carry a non-null condition string (e.g. "Yes"/
    // "No"); WorkflowEdge.tsx already forwards this top-level `label` straight into BaseEdge.
    // undefined (not null) so React Flow's own default (no label) applies when there's none.
    label: e.condition ?? undefined,
    labelStyle: EDGE_LABEL_STYLE,
    labelBgStyle: EDGE_LABEL_BG_STYLE,
    labelBgPadding: EDGE_LABEL_BG_PADDING,
    labelBgBorderRadius: 4,
    // WorkflowEdge.tsx (reused unchanged from Channel Workflow) only draws a dead-straight line
    // when layoutDirection is 'horizontal' AND isBranch is falsy — Channel Workflow computes a
    // real isBranch per edge (true fan-out/fan-in points) in its own displayEdges memo, but
    // always intends every edge to read as a soft curve regardless. Business Rules Workflow has
    // no equivalent memo and never set this field, so every edge fell into the straight case the
    // moment Auto-layout switched to horizontal. Setting it unconditionally true here forces the
    // same always-curved look Channel Workflow's reference style uses, without touching
    // WorkflowEdge.tsx itself — vertical mode is already always-bezier either way, so this only
    // changes horizontal mode's appearance.
    data: { condition: e.condition ?? null, isBranch: true },
  }));
}

export interface RuleFlowCanvasProps {
  channelId: string;
  flow: RuleFlow;
  /** PR #635 review (L2) — lets the parent (ChannelRuleFlowsView) disable "Re-load" while an
   * edit's serialize -> save -> confirm sequence is in flight, so a re-parse can't swap in a
   * fresh flow (new id, this component remounts via key={flow.id}) out from under an edit
   * that's still running against the old channelId/flowId/selectedNodeId closure. */
  onPersistingChange?: (isPersisting: boolean) => void;
  /** Channel-level "Re-load" action (re-parses every flow, not just this one) — rendered here,
   * right-aligned in the header next to the flow name, rather than in RuleFlowRail. Optional so a
   * caller that has no re-parse concept (e.g. a future embed) can simply omit it and get no button. */
  onReparse?: () => void;
  /** True while a re-parse (or an edit's serialize/save/confirm sequence — see
   * ChannelRuleFlowsView's isEditPersisting) is in flight; disables the button so a second
   * re-parse can't fire mid-request or race an in-flight edit. */
  isReparsing?: boolean;
  /** Fired with the recomputed plain-English rules text right after a recipient edit's confirm
   * step succeeds — the Channel Config tab is a sibling that stays mounted (just hidden) for the
   * whole drawer session and never refetches on its own, so without this it kept showing
   * whatever text the drawer was opened with until fully closed and reopened. */
  onBusinessRulesChanged?: (updatedText: string) => void;
  /** FIX 11b (frontend handover) — fires whenever this flow's pending-structural-edit list
   * (FIX 11) goes from empty to non-empty or back, keyed by flow.id since a rail row-click swaps
   * this whole component out (remounts via key={flow.id}) — the parent needs to know WHICH flow
   * is dirty, not just whether the currently-mounted instance is, to guard navigation correctly. */
  onDirtyChange?: (flowId: string, isDirty: boolean) => void;
  /** business_rules_workflow_count — this flow's execution-count metrics, looked up by the parent
   * from the channel-wide flow-runs response. Optional: omitted entirely (no label shown) when
   * metrics haven't loaded or the lookup fails, rather than showing a misleading zero. */
  flowRun?: FlowRun;
  /** business_rules_workflow_count — lets the header's "Runs: N" label jump straight to the
   * Channel Operations Dashboard's per-flow breakdown (a different drawer entirely, so this is
   * threaded all the way down from ThreadDetailPage). Omitted entirely (plain, non-clickable text)
   * when no caller wires it up — e.g. a future embed with no dashboard concept. */
  onOpenDashboard?: () => void;
}

/** FIX 11b (frontend handover) — imperative escape hatch for the parent's navigation guard
 * (rail row-click, drawer tab-switch, drawer close): those actions happen outside this
 * component's own render, so the parent needs a way to trigger the exact same Save/Discard
 * this component's own banner buttons use, then wait for the result before navigating. */
export interface RuleFlowCanvasHandle {
  saveEdits: () => Promise<boolean>;
  discardEdits: () => void;
  /** Lets the parent's navigation-guard dialog name WHAT is unsaved, not just how many changes —
   * one entry per pending diff, using the same nodeLabel already captured display-only at staging
   * time (PendingNodeEditDiff's own doc: "never sent to the backend"). */
  getPendingSummaries: () => string[];
}

interface RuleFlowSurfaceProps {
  nodes: Node<RuleNodeData>[];
  edges: Edge<WorkflowEdgeData>[];
  onNodesChange: ReturnType<typeof useNodesState<RuleNodeData>>[2];
  onEdgesChange: ReturnType<typeof useEdgesState<WorkflowEdgeData>>[2];
  onConnect: (connection: Connection) => void;
  onNodeClick: (_event: React.MouseEvent, node: Node) => void;
  onPaneClick: () => void;
  /** Re-runs the same layered layout used for nodes missing a position (ADR §2: position is a
   * canvas-only hint, never interpreted by the backend), applied to every node regardless of
   * its current position — lets an admin reset a manually-dragged-messy layout back to a clean
   * one. Purely client-side; nothing is sent to or persisted by the backend. */
  onAutoLayout: () => void;
}

const RuleFlowSurface: React.FC<RuleFlowSurfaceProps> = ({
  nodes, edges, onNodesChange, onEdgesChange, onConnect, onNodeClick, onPaneClick, onAutoLayout,
}) => {
  const { fitView } = useReactFlow();
  const initialFitDone = useRef(false);
  // Drives the lock/interactive toggle ourselves (instead of the default Controls button) only
  // so we can give it the custom tooltip text below — functionally identical to what the
  // default "toggle interactivity" button already did.
  const [interactive, setInteractive] = useState(true);

  useEffect(() => {
    if (!initialFitDone.current && nodes.length > 0) {
      initialFitDone.current = true;
      window.setTimeout(() => fitView({ padding: 0.2, duration: 300 }), 50);
    }
  }, [nodes.length, fitView]);

  const handleAutoLayoutClick = () => {
    onAutoLayout();
    // Positions update async via React state — wait a tick so ReactFlow has the new
    // coordinates before fitView measures the graph's bounding box (same idiom
    // WorkflowCanvasTab.tsx's own Auto-layout button already uses).
    window.setTimeout(() => fitView({ padding: 0.2, duration: 300 }), 50);
  };

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        nodesDraggable={interactive}
        nodesConnectable={interactive}
        elementsSelectable={interactive}
        fitView
        fitViewOptions={{ padding: 0.2 }}
      >
        <Background />
        {/* Default zoom in/out and fit view buttons already carry native title attributes out of
            the box. Only the lock/interactive button is replaced (showInteractive={false} hides
            the default one) so it can carry the custom explanation text below instead of the
            generic "toggle interactivity". */}
        <Controls showInteractive={false}>
          <ControlButton title={INTERACTIVE_TOGGLE_TOOLTIP} onClick={() => setInteractive((v) => !v)}>
            {interactive ? <LockOpenIcon sx={{ fontSize: 16 }} /> : <LockIcon sx={{ fontSize: 16 }} />}
          </ControlButton>
        </Controls>
        <Panel position="top-right">
          <Button
            size="small"
            variant="contained"
            onClick={handleAutoLayoutClick}
            disabled={nodes.length === 0}
            title="Automatically arranges nodes top-to-bottom or left-to-right (alternates each click) and re-centers the view"
            sx={{ textTransform: 'none' }}
          >
            Auto-layout
          </Button>
        </Panel>
      </ReactFlow>
    </div>
  );
};

/**
 * Business_rule_canvas branch (PRISM7_CHANNEL_RULES_CANVAS_TDD.md §8.2) — one flow's fully
 * editable canvas, rendered in the center pane while the rail stays visible on the left. Reuses
 * WorkflowEdge.tsx unchanged; nodes render via RuleNode.tsx (TRIGGER/ACTION/CONDITION), not
 * PrimitiveNode.tsx.
 *
 * Edit sequence (TDD §2 rows 9-12 / §5.2, compute/confirm split — must be exact):
 *   1. serializeRuleFlow with ONLY the edited node (never the whole graph) — compute only, no write.
 *   2. saveChannelBusinessRules with serialize's updated_channel_business_rules (the normal,
 *      already-built save/validate path).
 *   3. Only on success of step 2, confirmRuleFlow with { updated_flow, pre_confirm_hash } from
 *      step 1 — persists the structured flow.
 * If step 2 fails, stop — confirmRuleFlow is never called, so nothing is left half-saved.
 *
 * If confirm (step 3) 409s (TDD §5.2/§4.2: the flow changed server-side since serialize ran —
 * e.g. a concurrent re-parse), the canvas goes read-only and shows a banner asking for a
 * re-parse, reactively — never shown preemptively, since nothing proactively knows about
 * staleness until an edit is actually attempted (TDD §8.2, RuleFlowRail row, Finding 2).
 */
const RuleFlowCanvas = forwardRef<RuleFlowCanvasHandle, RuleFlowCanvasProps>(({ channelId, flow, onPersistingChange, onReparse, isReparsing, onBusinessRulesChanged, onDirtyChange, flowRun, onOpenDashboard }, ref) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<RuleNodeData>(docToRfNodes(flow.nodes, flow.edges));
  const [edges, setEdges, onEdgesChange] = useEdgesState<WorkflowEdgeData>(docToRfEdges(flow.edges));
  // Bug found live — sticky across a resync, deliberately NOT recomputed fresh every time.
  // docToRfNodes' own hasRecipientConcept is correct as a per-call snapshot (and stays a pure
  // function other tests rely on), but handleRecipientsChange re-syncs `nodes` straight from the
  // SERVER's response once serialize/confirm succeed — and the server legitimately reports an
  // empty recipients array once the admin clears it, recomputing hasRecipientConcept: false all
  // over again the moment that resync lands. This ref remembers every node id that has EVER
  // reported true, so displayNodes below can keep the RecipientPicker visible regardless of what
  // a later resync's own snapshot says.
  const recipientConceptIdsRef = useRef<Set<string>>(
    new Set(docToRfNodes(flow.nodes, flow.edges).filter((n) => n.data.hasRecipientConcept).map((n) => n.id))
  );
  useEffect(() => {
    nodes.forEach((n) => {
      if (n.data.hasRecipientConcept) recipientConceptIdsRef.current.add(n.id);
    });
  }, [nodes]);
  const [flowId, setFlowId] = useState(flow.id);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [isPersisting, setIsPersisting] = useState(false);
  const [persistError, setPersistError] = useState<string | null>(null);
  const [isStale, setIsStale] = useState(false);
  // Alternates each Auto-layout click, same idiom as WorkflowCanvasTab.tsx's own Auto-layout —
  // reuses that file's LayoutDirectionContext (a plain, stateless context object) purely for
  // WorkflowEdge.tsx's already-existing straight-vs-bezier logic and RuleNode's handle sides;
  // providing it here doesn't read or write anything in the legacy canvas.
  const [layoutDirection, setLayoutDirection] = useState<LayoutDirection>('vertical');

  // FIX 9/11 (frontend handover) — staged structural edits (rename/insert), zero network calls
  // until Save. Never mutates `nodes`/`edges` directly (see displayNodes/displayEdges below) —
  // pending rename/insert data is only ever a derived overlay, so a failed or discarded save has
  // nothing to roll back: the real nodes/edges state was never touched in the first place.
  const [pendingDiffs, setPendingDiffs] = useState<PendingDiff[]>([]);
  const [isSavingStructural, setIsSavingStructural] = useState(false);
  const [insertDialogEdgeId, setInsertDialogEdgeId] = useState<string | null>(null);
  const [insertNodeType, setInsertNodeType] = useState<'ACTION' | 'CONDITION'>('ACTION');
  const [insertLabel, setInsertLabel] = useState('');
  const [insertText, setInsertText] = useState('');
  const [insertBranchLabel, setInsertBranchLabel] = useState('');
  // Insert now saves immediately (see handleSubmitInsert's own doc) rather than staging, so it
  // needs its own in-flight/error state — scoped to the dialog itself (shown inside it) rather
  // than reusing persistError, which renders on the main canvas behind this modal.
  const [isInserting, setIsInserting] = useState(false);
  const [insertError, setInsertError] = useState<string | null>(null);
  // Delete-node also saves immediately (see handleDeleteNode's own doc) — no dialog of its own to
  // scope a loading/error state to (it fires straight from the edge's icon), so this tracks
  // which node ids currently have a delete request in flight, purely to dim the card on the
  // canvas while it's happening. A Set, not a single id, since two different edges could in
  // theory be clicked in quick succession before the first request resolves.
  const [deletingNodeIds, setDeletingNodeIds] = useState<Set<string>>(new Set());
  // FIX 9b — local input buffer for the inspector's rename field, seeded from whatever's
  // already staged for the selected node (or the node's real label) each time selection changes.
  const [renameLabel, setRenameLabel] = useState('');
  // PRISM7_CHANNEL_RULES_NODE_DESCRIPTIONS_TDD §6 — the Description field's own draft, saved via
  // its own PATCH-only path (handleSaveDescription), entirely separate from the Label rename
  // above: a description edit is an annotation, never a business-rules edit, so it never enters
  // pendingDiffs/serialize/confirm.
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);

  // PR #635 review (L2) — this instance fully remounts (key={flow.id}) whenever the rail swaps
  // in a different flow (e.g. a re-parse mid-edit assigns new ids), so an in-flight edit's
  // promise chain must stop applying local state / calling confirm once *this* instance is
  // gone, rather than resolving against a channelId/flowId/selectedNodeId closure the user can
  // no longer see or affect.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    onPersistingChange?.(isPersisting);
  }, [isPersisting, onPersistingChange]);

  // FIX 11b — report dirty state keyed by flow.id (see prop doc above) whenever the pending list
  // changes, including on unmount (e.g. the rail swaps to a different flow while this one still
  // has unsaved edits — the guard needs to know this flow is dirty even after this instance is
  // gone, since ChannelRuleFlowsView, not this component, owns which flow is currently mounted).
  useEffect(() => {
    onDirtyChange?.(flow.id, pendingDiffs.length > 0);
  }, [pendingDiffs.length, flow.id, onDirtyChange]);

  const onConnect = useCallback(
    (connection: Connection) =>
      setEdges((eds) => addEdge({ ...connection, type: WORKFLOW_EDGE_RF_TYPE, data: { condition: null } }, eds)),
    [setEdges]
  );

  // Client-side only — re-lays out every current node (not just ones missing a position),
  // reusing the same layered algorithm docToRfNodes already applies as a fallback. Positions are
  // a canvas-only rendering hint the backend never interprets or stores, so this never needs an
  // API call.
  const handleAutoLayout = useCallback(() => {
    const nextDirection: LayoutDirection = layoutDirection === 'vertical' ? 'horizontal' : 'vertical';
    setNodes((nds) => {
      const positions = computeFallbackPositions(
        nds.map((n) => n.id),
        edges.map((e) => ({ source: e.source, target: e.target })),
        nextDirection
      );
      return nds.map((n) => (positions[n.id] ? { ...n, position: positions[n.id] } : n));
    });
    setLayoutDirection(nextDirection);
  }, [edges, setNodes, layoutDirection]);

  // FIX 9b/11 — a pending rename overlays its label onto the rendered node without touching the
  // real `nodes` state, so discarding is free (just clear pendingDiffs) and a save failure leaves
  // nothing to unwind (nothing was ever actually mutated).
  const displayNodes = useMemo(
    () =>
      nodes.map((n) => {
        const pendingEdit = pendingDiffs.find(
          (d): d is PendingNodeEditDiff => d.kind === 'edited_node' && d.nodeId === n.id
        );
        // FIX 13-FE — delete now saves immediately (handleDeleteNode), so this just dims the card
        // for the brief window its own request is actually in flight — there's nothing to
        // "restore via Discard" anymore, unlike the earlier staged design.
        const pendingDelete = deletingNodeIds.has(n.id);
        // Bug found live — sticky-OR against recipientConceptIdsRef (see its own doc above),
        // computed unconditionally so a node with no pending edit/delete still gets patched
        // when a resync's fresh snapshot alone would have gone back to false.
        const hasRecipientConcept = n.data.hasRecipientConcept || recipientConceptIdsRef.current.has(n.id);
        if (!pendingEdit && !pendingDelete && hasRecipientConcept === n.data.hasRecipientConcept) {
          return n;
        }
        return {
          ...n,
          data: {
            ...n.data,
            label: pendingEdit?.label ?? n.data.label,
            isPendingEdit: Boolean(pendingEdit),
            pendingDelete,
            hasRecipientConcept,
          },
        };
      }),
    [nodes, pendingDiffs, deletingNodeIds]
  );

  // FIX 9a — wires WorkflowEdge.tsx's already-existing (previously unwired) "Insert step here"
  // hover affordance to actually open the form.
  const handleOpenInsertDialog = useCallback((edgeId: string) => {
    setInsertDialogEdgeId(edgeId);
    setInsertNodeType('ACTION');
    setInsertLabel('');
    setInsertText('');
    setInsertBranchLabel('');
    setInsertError(null);
  }, []);

  // FIX 13-FE (delete-node) — saves immediately on click, per explicit request (mirrors
  // handleSubmitInsert's own reasoning: the edge's delete icon is its own single, self-contained
  // confirm step, unlike a rename typed directly onto the canvas). The client-side branching-node
  // check here mirrors a subset of the backend's own validation purely for a fast, friendly error
  // before an obviously-doomed request — it does NOT replace the backend's check, which stays
  // authoritative since edges can change between this check and the request actually landing.
  //
  // Consolidated onto WorkflowEdge.tsx's own hover-toolbar delete icon per explicit request — the
  // inspector panel's separate delete button was removed, so this takes an explicit nodeId rather
  // than reading selectedNodeId: the edge-hover affordance has nothing to do with whatever node
  // (if any) is currently selected.
  const handleDeleteNode = useCallback(
    async (nodeId: string) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node || node.data.nodeType === 'TRIGGER') return;
      const outgoingCount = edges.filter((e) => e.source === nodeId).length;
      if (outgoingCount >= 2) {
        setPersistError('This node branches into multiple paths — delete its branch targets first, then this node.');
        return;
      }

      setDeletingNodeIds((prev) => new Set(prev).add(nodeId));
      setPersistError(null);
      try {
        const serialized = await serializeRuleFlow(channelId, flowId, { delete_node: { node_id: nodeId } });
        await saveChannelBusinessRules(channelId, serialized.updated_channel_business_rules);
        onBusinessRulesChanged?.(serialized.updated_channel_business_rules);

        if (!isMountedRef.current) return;

        await confirmRuleFlow(channelId, flowId, {
          updated_flow: serialized.updated_flow,
          pre_confirm_hash: serialized.pre_confirm_hash,
        });

        if (!isMountedRef.current) return;

        setFlowId(serialized.updated_flow.id);
        setNodes(docToRfNodes(serialized.updated_flow.nodes, serialized.updated_flow.edges, layoutDirection));
        setEdges(docToRfEdges(serialized.updated_flow.edges));
        // Only clears the inspector if it happened to be showing the node that just got deleted —
        // otherwise unrelated to whatever else is currently selected.
        setSelectedNodeId((current) => (current === nodeId ? null : current));
      } catch (err) {
        if (!isMountedRef.current) return;
        if (err instanceof RuleFlowNodeNotFoundError) {
          // Already gone (e.g. a concurrent edit deleted it first) — the end state the admin
          // wanted already holds, so surfacing this as an error would be misleading.
        } else if (err instanceof RuleFlowConfirmConflictError) {
          setIsStale(true);
        } else {
          setPersistError(err instanceof Error ? err.message : 'Failed to delete this step. Please try again.');
        }
      } finally {
        if (isMountedRef.current) {
          setDeletingNodeIds((prev) => {
            const next = new Set(prev);
            next.delete(nodeId);
            return next;
          });
        }
      }
    },
    [nodes, edges, channelId, flowId, layoutDirection, setNodes, setEdges, onBusinessRulesChanged]
  );

  // Resolves "delete this edge" to "delete the node it points to" — the edge itself isn't what's
  // being removed; its target node is (see handleDeleteNode's own doc for why this consumed the
  // inspector panel's former delete affordance instead of staying a separate feature).
  const handleDeleteEdgeTargetNode = useCallback(
    (edgeId: string) => {
      const edge = edges.find((e) => e.id === edgeId);
      if (!edge) return;
      void handleDeleteNode(edge.target);
    },
    [edges, handleDeleteNode]
  );

  const displayEdges = useMemo(
    () =>
      edges.map((e) => ({
        ...e,
        data: {
          ...e.data,
          onInsert: handleOpenInsertDialog,
          onDelete: handleDeleteEdgeTargetNode,
          // Data-driven with a safe default (WorkflowEdge.tsx falls back to "Delete connection"
          // when this is unset) rather than editing that shared component's hardcoded string —
          // WorkflowCanvasTab.tsx's legacy canvas wires its own onDelete to genuinely delete an
          // edge there, where that exact wording is still correct; this override only ever
          // applies to edges built by THIS canvas's own displayEdges.
          deleteTooltip: 'Delete this step',
        },
      })),
    [edges, handleOpenInsertDialog, handleDeleteEdgeTargetNode]
  );

  const insertDialogEdge = useMemo(
    () => (insertDialogEdgeId ? edges.find((e) => e.id === insertDialogEdgeId) ?? null : null),
    [edges, insertDialogEdgeId]
  );
  const insertDialogSourceNode = useMemo(
    () => (insertDialogEdge ? nodes.find((n) => n.id === insertDialogEdge.source) ?? null : null),
    [nodes, insertDialogEdge]
  );
  // "only when the edge being inserted on originates from a CONDITION node" (FIX 9a).
  const insertDialogNeedsBranchLabel = insertDialogSourceNode?.data.nodeType === 'CONDITION';

  // Ignored while an insert is actually in flight — closing (backdrop/Escape/Cancel) mid-request
  // would leave the admin thinking they canceled while the network call keeps running regardless.
  const handleCloseInsertDialog = useCallback(() => {
    if (isInserting) return;
    setInsertDialogEdgeId(null);
  }, [isInserting]);

  // Insert-step saves immediately on this click — explicitly requested: the dialog is already
  // its own confirm step (the admin filled a form and clicked a button meaning "do this"), unlike
  // a rename typed directly onto the canvas with no separate confirmation of its own, so staging
  // it into the pending-changes banner alongside renames added a redundant second confirm step
  // for no benefit. Mirrors handleRecipientsChange's own serialize -> save -> confirm sequence;
  // the only difference is there's no optimistic local write first (there is no node to update
  // yet — it doesn't exist until the server creates it), so the new node simply appears once
  // confirm succeeds and the canvas re-syncs, same as it already did after a batched Save before
  // this change.
  const handleSubmitInsert = useCallback(async () => {
    if (!insertDialogEdge || !insertLabel.trim() || !insertText.trim() || isStale) return;

    const request = {
      insert_node: {
        after_node_id: insertDialogEdge.source,
        before_node_id: insertDialogEdge.target,
        node_type: insertNodeType,
        label: insertLabel.trim(),
        text: insertText.trim(),
        edge_condition: insertDialogNeedsBranchLabel ? insertBranchLabel.trim() || undefined : undefined,
      },
    };

    setIsInserting(true);
    setIsPersisting(true);
    setInsertError(null);
    try {
      const serialized = await serializeRuleFlow(channelId, flowId, request);
      await saveChannelBusinessRules(channelId, serialized.updated_channel_business_rules);
      onBusinessRulesChanged?.(serialized.updated_channel_business_rules);

      if (!isMountedRef.current) return;

      await confirmRuleFlow(channelId, flowId, {
        updated_flow: serialized.updated_flow,
        pre_confirm_hash: serialized.pre_confirm_hash,
      });

      if (!isMountedRef.current) return;

      setFlowId(serialized.updated_flow.id);
      setNodes(docToRfNodes(serialized.updated_flow.nodes, serialized.updated_flow.edges, layoutDirection));
      setEdges(docToRfEdges(serialized.updated_flow.edges));
      setInsertDialogEdgeId(null);
    } catch (err) {
      if (!isMountedRef.current) return;
      if (err instanceof RuleFlowConfirmConflictError) {
        // Matches the existing stale-flow handling elsewhere — further edits are blocked until
        // re-parse, so there's nothing left for this dialog to do; close it rather than leave it
        // open in front of the canvas's own read-only banner.
        setIsStale(true);
        setInsertDialogEdgeId(null);
      } else {
        setInsertError(err instanceof Error ? err.message : 'Failed to insert this step. Please try again.');
      }
    } finally {
      if (isMountedRef.current) {
        setIsInserting(false);
        setIsPersisting(false);
      }
    }
  }, [
    insertDialogEdge,
    insertNodeType,
    insertLabel,
    insertText,
    insertBranchLabel,
    insertDialogNeedsBranchLabel,
    channelId,
    flowId,
    isStale,
    layoutDirection,
    setNodes,
    setEdges,
    onBusinessRulesChanged,
  ]);

  // FIX 9b — stages (or updates, or clears) a rename/reword edit for the currently selected node.
  // Coalesces into a single pending diff per node id, matching the backend's "exactly one
  // edited_node per call" contract — repeatedly editing the same node before Save just replaces
  // its one pending entry rather than accumulating duplicates.
  //
  // Returns the freshly-computed array (in addition to applying it via setPendingDiffs) so a
  // caller that needs to act on it in the SAME tick — the inspector panel's own Save button,
  // which may fire before any blur has ever staged this edit — doesn't have to wait for a
  // re-render to see it; setPendingDiffs' own update wouldn't be visible to
  // handleSaveStructuralEdits' closure until then otherwise.
  const stageRename = useCallback((): PendingDiff[] => {
    if (!selectedNodeId) return pendingDiffs;
    const node = nodes.find((n) => n.id === selectedNodeId);
    if (!node) return pendingDiffs;
    const trimmedLabel = renameLabel.trim();
    const label = trimmedLabel && trimmedLabel !== node.data.label ? trimmedLabel : undefined;
    const withoutThisNode = pendingDiffs.filter((d) => !(d.kind === 'edited_node' && d.nodeId === selectedNodeId));
    const next = !label
      ? withoutThisNode
      : [
          ...withoutThisNode,
          { kind: 'edited_node', nodeId: selectedNodeId, nodeLabel: node.data.label, label } as PendingNodeEditDiff,
        ];
    setPendingDiffs(next);
    return next;
  }, [selectedNodeId, nodes, renameLabel, pendingDiffs]);

  const handleStageRename = useCallback(() => {
    stageRename();
  }, [stageRename]);

  const handleDiscardPendingDiffs = useCallback(() => setPendingDiffs([]), []);

  // FIX 11 — Save fires every pending diff sequentially (the doc's recommended starting option:
  // "needs nothing from the backend beyond what FIX 5-7 already ship"), each through the exact
  // same serialize -> save -> confirm sequence recipient edits already use. On failure, whatever
  // hasn't run yet (including the one that just failed) stays pending so the admin can retry;
  // anything that already succeeded is gone for good (each edit is independently correct, per
  // the doc's own framing of the sequential approach).
  //
  // PR #642 review (M1) — this loop used to track "what's left" in a local `remaining` variable
  // and write it back with setPendingDiffs(remaining) after each item, overwriting the ENTIRE
  // pendingDiffs array with that stale local snapshot. Staging a brand-new edit on a different
  // node while a save was still in flight landed in state correctly (via a functional update),
  // but the very next iteration's setPendingDiffs(remaining) then clobbered it right back out,
  // silently — remaining never knew that edit existed. The same overwrite could also resurrect a
  // diff FIX 11b's guard dialog had just discarded, if its "Discard & continue" fired mid-save.
  //
  // diffsToProcess is still a one-time snapshot — intentionally: anything staged AFTER Save was
  // clicked belongs to a later Save, not this one. What changed is that a completed diff now
  // removes only ITSELF from whatever pendingDiffs actually is when that update applies
  // (matched by object identity, not index/count), via setPendingDiffs(prev => ...). That can
  // never clobber a concurrent addition or an explicit discard, because it never reads or
  // overwrites the rest of the array — only prunes the one entry it just finished.
  //
  // diffsOverride — the inspector panel's own Save button can fire before any blur has ever
  // staged the field currently being typed into (see stageRename's own doc); passing its
  // synchronously-computed return value here lets that live edit be included immediately,
  // instead of silently saving everything EXCEPT it because pendingDiffs' state update hasn't
  // been reflected in this closure yet. Every other caller (the banner's Save, the guard dialog's
  // Save & continue) omits it and gets the ordinary closure-captured pendingDiffs.
  const handleSaveStructuralEdits = useCallback(async (diffsOverride?: PendingDiff[]): Promise<boolean> => {
    setIsSavingStructural(true);
    setPersistError(null);
    const diffsToProcess = diffsOverride ?? pendingDiffs;
    try {
      for (const diff of diffsToProcess) {
        // Only ever 'edited_node' — insert-step and delete-node both save immediately elsewhere
        // (handleSubmitInsert, handleDeleteNode) and never enter pendingDiffs. Never carries
        // `text` — that field now belongs solely to the description PATCH path
        // (handleSaveDescription), which never touches business rules.
        const request = { edited_node: { id: diff.nodeId, label: diff.label } };
        const serialized = await serializeRuleFlow(channelId, flowId, request);
        await saveChannelBusinessRules(channelId, serialized.updated_channel_business_rules);
        onBusinessRulesChanged?.(serialized.updated_channel_business_rules);

        if (!isMountedRef.current) return false;

        await confirmRuleFlow(channelId, flowId, {
          updated_flow: serialized.updated_flow,
          pre_confirm_hash: serialized.pre_confirm_hash,
        });

        if (!isMountedRef.current) return false;

        setFlowId(serialized.updated_flow.id);
        setNodes(docToRfNodes(serialized.updated_flow.nodes, serialized.updated_flow.edges, layoutDirection));
        setEdges(docToRfEdges(serialized.updated_flow.edges));

        // Removes exactly this diff from live state — never touches anything staged or
        // discarded concurrently with this iteration's await above (PR #642 review M1).
        setPendingDiffs((prev) => prev.filter((d) => d !== diff));
      }
      return true;
    } catch (err) {
      if (!isMountedRef.current) return false;
      // Nothing to "put back" here — every diff that finished already removed only itself above,
      // and this one (plus anything after it, or staged concurrently) was never touched, so live
      // pendingDiffs already correctly reflects what's still unsaved.
      if (err instanceof RuleFlowConfirmConflictError) {
        setIsStale(true);
      } else {
        setPersistError(err instanceof Error ? err.message : 'Failed to save pending changes. Please try again.');
      }
      return false;
    } finally {
      if (isMountedRef.current) setIsSavingStructural(false);
    }
  }, [pendingDiffs, channelId, flowId, layoutDirection, setNodes, setEdges, onBusinessRulesChanged]);

  // FIX 11b — the parent's navigation guard dialog can't call the component's own internal
  // handlers directly (they're outside React's render), so this exposes the same two actions
  // the pending-changes banner's own Save/Discard buttons already call.
  useImperativeHandle(ref, () => ({
    saveEdits: handleSaveStructuralEdits,
    discardEdits: handleDiscardPendingDiffs,
    getPendingSummaries: () => pendingDiffs.map((d) => d.nodeLabel),
  }), [handleSaveStructuralEdits, handleDiscardPendingDiffs, pendingDiffs]);

  // Derives from displayNodes (not raw nodes) so the inspector header reflects a pending rename
  // immediately, matching the same node the canvas card itself shows.
  const selectedNode = useMemo(
    () => displayNodes.find((n) => n.id === selectedNodeId) ?? null,
    [displayNodes, selectedNodeId]
  );

  // FIX 13-FE (delete-node handover doc §3.5) — a node staged for deletion is deselected the
  // instant it's staged (handleStageDelete), but the admin can still re-select it afterward
  // (it's still visible, strikethrough, on the canvas) — this is what that revisit case reads.
  const isSelectedNodePendingDelete = Boolean(selectedNode?.data.pendingDelete);

  // True the instant the inspector's own fields actually differ from this node's real, saved
  // state — including while still typing, before any blur has staged anything via
  // handleStageRename. Deliberately NOT the same thing as selectedNode.data.isPendingEdit, which
  // only turns true after a blur; this is what lets the inspector's own Save button (below) show
  // up earlier than that, matching the ask that it appear "when the user starts to modify", not
  // only once they've already clicked away.
  //
  // Compared against `nodes` (the real, un-staged data), not `selectedNode`/`displayNodes` —
  // displayNodes overlays an already-staged label onto the very same node, so comparing against
  // that would make an already-staged-but-revisited node wrongly look unchanged the moment its
  // label happens to match its own pending value.
  const hasLiveInspectorEdit = Boolean(
    selectedNodeId &&
      (() => {
        const realNode = nodes.find((n) => n.id === selectedNodeId);
        if (!realNode) return false;
        return renameLabel.trim() !== '' && renameLabel.trim() !== realNode.data.label;
      })()
  );

  // PRISM7_CHANNEL_RULES_NODE_DESCRIPTIONS_TDD §6 — Save/Cancel appear only once the draft
  // actually differs from the persisted server value; an absent persisted description compares
  // against '' so a first-time description on a historical node still counts as dirty.
  const isDescriptionDirty = Boolean(
    selectedNode && descriptionDraft.trim() !== (selectedNode.data.description ?? '').trim()
  );

  // FIX 9b — reseed the rename input buffer from whatever's already staged for this node (or its
  // real label) every time selection changes, so reopening an already-staged node shows the
  // in-progress edit instead of resetting it. The description draft reseeds from the persisted
  // server value instead (TDD §6) — it has no pendingDiffs concept of its own since it never
  // stages, it only ever reads/writes straight through PATCH.
  useEffect(() => {
    if (!selectedNodeId) return;
    const pendingEdit = pendingDiffs.find(
      (d): d is PendingNodeEditDiff => d.kind === 'edited_node' && d.nodeId === selectedNodeId
    );
    const node = nodes.find((n) => n.id === selectedNodeId);
    setRenameLabel(pendingEdit?.label ?? node?.data.label ?? '');
    setDescriptionDraft(node?.data.description ?? '');
    setDescriptionError(null);
    // Only reseed when the SELECTION changes, not on every pendingDiffs/nodes change — otherwise
    // typing would keep getting clobbered by this same effect re-running mid-keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId]);

  // PRISM7_CHANNEL_RULES_NODE_DESCRIPTIONS_TDD §3.2/§6 — the only network call a description edit
  // ever makes; never serializeRuleFlow/saveChannelBusinessRules/confirmRuleFlow, so it stays
  // available even while the flow is stale/business-rule editing is blocked.
  const handleSaveDescription = useCallback(async () => {
    if (!selectedNodeId) return;
    const trimmed = descriptionDraft.trim();
    if (!trimmed) return;
    setIsSavingDescription(true);
    setDescriptionError(null);
    try {
      const updatedFlow = await updateNodeDescription(channelId, flowId, selectedNodeId, trimmed);
      if (!isMountedRef.current) return;
      // Resync from the server's own response (description_source flips to 'user',
      // description_rules_hash clears) rather than writing the local draft in optimistically.
      setNodes(docToRfNodes(updatedFlow.nodes, updatedFlow.edges, layoutDirection));
      setEdges(docToRfEdges(updatedFlow.edges));
    } catch (err) {
      if (!isMountedRef.current) return;
      // Nothing was written locally above, so the prior persisted display is already intact —
      // just surface the error and keep the user's typed draft so it isn't lost.
      if (err instanceof RuleFlowDescriptionNotFoundError) {
        setDescriptionError('This node no longer exists — re-load the flow.');
      } else {
        setDescriptionError(err instanceof Error ? err.message : 'Failed to save this description. Please try again.');
      }
    } finally {
      if (isMountedRef.current) setIsSavingDescription(false);
    }
  }, [selectedNodeId, descriptionDraft, channelId, flowId, layoutDirection, setNodes, setEdges]);

  // Cancel restores the server value with no API call (TDD §6 FE tests).
  const handleCancelDescription = useCallback(() => {
    setDescriptionDraft(selectedNode?.data.description ?? '');
    setDescriptionError(null);
  }, [selectedNode]);

  const handleRecipientsChange = useCallback(
    async (recipients: RuleFlowRecipient[]) => {
      if (!selectedNodeId || isStale) return;

      // FIX 3 (frontend handover) — reflect the pick on the canvas immediately, before any
      // network call, so the UI never visibly blocks on the serialize/save/confirm round trip.
      // previousRecipients is captured here (from the node's state right before this optimistic
      // write) so the catch block below can put it back exactly if the chain fails.
      let previousRecipients: RuleFlowRecipient[] | undefined;
      setNodes((nds) =>
        nds.map((n) => {
          if (n.id !== selectedNodeId) return n;
          previousRecipients = n.data.recipients;
          return { ...n, data: { ...n.data, recipients } };
        })
      );

      setIsPersisting(true);
      setPersistError(null);
      try {
        // Step 1 — compute only, no write. Only the single edited node is sent, never the
        // whole graph (TDD §5.2).
        const serialized = await serializeRuleFlow(channelId, flowId, {
          edited_node: { id: selectedNodeId, recipients },
        });

        // Step 2 — the normal, already-built save/validate path. If this throws, we stop here:
        // confirmRuleFlow below is never reached, so nothing is left half-saved.
        await saveChannelBusinessRules(channelId, serialized.updated_channel_business_rules);

        // The rules text is now the persisted source of truth on the backend, regardless of
        // whether confirmRuleFlow below succeeds — fire this right away so the still-mounted
        // Config tab never keeps showing stale text if confirm then 409s (e.g. a concurrent
        // re-parse), which could otherwise lead to it overwriting the backend's newer text.
        onBusinessRulesChanged?.(serialized.updated_channel_business_rules);

        // A re-parse elsewhere may have already swapped this component out (new flow.id ->
        // remount) while the sequence above was in flight — don't call confirm against a flow
        // the user is no longer looking at, and don't touch state on an unmounted instance.
        if (!isMountedRef.current) return;

        // Step 3 — only reached because step 2 succeeded. Persists the structured flow.
        await confirmRuleFlow(channelId, flowId, {
          updated_flow: serialized.updated_flow,
          pre_confirm_hash: serialized.pre_confirm_hash,
        });

        if (!isMountedRef.current) return;

        // Re-sync the canvas from the server's own updated_flow rather than just patching the
        // one field locally — serialize/confirm may have adjusted other fields too (e.g. a
        // scoped LLM fallback rewording the node's label).
        setFlowId(serialized.updated_flow.id);
        setNodes(docToRfNodes(serialized.updated_flow.nodes, serialized.updated_flow.edges, layoutDirection));
        setEdges(docToRfEdges(serialized.updated_flow.edges));
      } catch (err) {
        if (!isMountedRef.current) return;
        // FIX 3 — nothing in the chain actually persisted (even a confirm 409 means the
        // structured flow was never confirmed), so the optimistic write above must not be left
        // showing on the canvas.
        setNodes((nds) =>
          nds.map((n) => (n.id === selectedNodeId ? { ...n, data: { ...n.data, recipients: previousRecipients } } : n))
        );
        if (err instanceof RuleFlowConfirmConflictError) {
          setIsStale(true);
        } else {
          setPersistError(err instanceof Error ? err.message : 'Failed to save this change. Please try again.');
        }
      } finally {
        if (isMountedRef.current) setIsPersisting(false);
      }
    },
    [channelId, flowId, isStale, selectedNodeId, setEdges, setNodes, onBusinessRulesChanged, layoutDirection]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {flow.flow_name}
        </Typography>
        {flowRun && (
          // Compact text (not the big filled pill chip this replaced) — admin feedback was that
          // the chip was too visually heavy for the header. Clickable (when onOpenDashboard is
          // wired up) to jump straight to the Channel Operations Dashboard's per-flow breakdown.
          <Tooltip
            title={
              `${flowRun.completed_count} completed, ${flowRun.failed_count} failed, ` +
              `${flowRun.approval_requested_count} awaiting approval` +
              (flowRun.last_run_at ? ` — last run ${new Date(flowRun.last_run_at).toLocaleString()}` : '') +
              (onOpenDashboard ? ' — click to view in Dashboard' : '')
            }
          >
            <Box
              onClick={onOpenDashboard}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: 'primary.main',
                cursor: onOpenDashboard ? 'pointer' : 'default',
                '&:hover': onOpenDashboard ? { textDecoration: 'underline' } : {},
              }}
            >
              {/* lineHeight: 1 on both — caption's default line-height (1.66) padded extra space
                  above/below the glyphs that the icon doesn't have, so flex's alignItems:'center'
                  centered their boxes but left the text looking visually low next to the icon and
                  the flow name beside it. Tightening both to their own content height fixes it. */}
              <PlayCircleOutlineIcon sx={{ fontSize: '0.9rem', display: 'block', lineHeight: 1 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1 }}>
                Runs: {flowRun.run_count}
              </Typography>
            </Box>
          </Tooltip>
        )}
        {flowRun && (
          // Explicit info icon beside the label, same as the rail used to — discoverable without
          // first guessing the label itself is hoverable/clickable.
          <Tooltip title="How many times this workflow has been triggered and run in this channel.">
            <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        )}
        {onReparse && (
          <Button
            size="small"
            variant="contained"
            // Bug found live — a real re-parse is a genuine LLM round trip (tens of seconds,
            // same order as the parse endpoint's own worst-case latency), but this button
            // previously only went `disabled` with no other visual change — indistinguishable
            // from the click simply not having registered. Same spinner-in-startIcon convention
            // already used elsewhere in this file (isSavingStructural/isInserting above).
            startIcon={isReparsing ? <CircularProgress size={14} color="inherit" /> : <AutorenewIcon />}
            onClick={onReparse}
            disabled={isReparsing}
            sx={{ ml: 'auto', textTransform: 'none' }}
          >
            {isReparsing ? 'Reloading…' : 'Re-load'}
          </Button>
        )}
      </Box>

      {isStale && (
        <Alert severity="warning" sx={{ mx: 1.5, mt: 1.5 }}>
          Rules changed elsewhere — re-load to edit. Use the "Re-load" button above.
        </Alert>
      )}

      {persistError && (
        <Alert severity="error" sx={{ mx: 1.5, mt: 1.5 }} onClose={() => setPersistError(null)}>
          {persistError}
        </Alert>
      )}

      {/* FIX 11 (frontend handover) — pending structural edits (renames + inserts) sit in
          pendingDiffs until Save is clicked; nothing here has been sent to the backend yet. */}
      {pendingDiffs.length > 0 && (
        <Alert
          severity="info"
          sx={{ mx: 1.5, mt: 1.5 }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" onClick={handleDiscardPendingDiffs} disabled={isSavingStructural}>
                Discard
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => void handleSaveStructuralEdits()}
                disabled={isSavingStructural}
                startIcon={isSavingStructural ? <CircularProgress size={14} /> : undefined}
              >
                {isSavingStructural ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          }
        >
          {pendingDiffs.length} unsaved change{pendingDiffs.length > 1 ? 's' : ''} to this flow
        </Alert>
      )}

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Provides the same context WorkflowEdge.tsx already reads (unchanged) for its
              straight-vs-bezier decision, and RuleNode.tsx now reads for its handle sides — a
              plain, stateless React Context, so this has no effect on the legacy canvas that
              also happens to use it. */}
          <LayoutDirectionContext.Provider value={layoutDirection}>
            <ReactFlowProvider>
              <RuleFlowSurface
                nodes={displayNodes}
                edges={displayEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeClick={(_event, node) => setSelectedNodeId(node.id)}
                onPaneClick={() => setSelectedNodeId(null)}
                onAutoLayout={handleAutoLayout}
              />
            </ReactFlowProvider>
          </LayoutDirectionContext.Provider>
        </Box>

        {selectedNode && (
          <Paper
            elevation={0}
            sx={{
              width: 300,
              flexShrink: 0,
              // Sits in a flex row whose default align-items: stretch otherwise forces this
              // panel to the full canvas height regardless of content — leaving a large empty
              // area below a short TRIGGER/CONDITION node (no RecipientPicker to fill it).
              // flex-start lets it size to its own content instead.
              alignSelf: 'flex-start',
              maxHeight: '100%',
              overflowY: 'auto',
              borderLeft: '1px solid',
              borderColor: 'divider',
              p: 2.5,
            }}
          >
            {/* Heading + type badge share the header row's own subtitle1/600 weight and
                border-bottom divider (matching this component's own flow-name header above),
                and the badge reuses RuleNode's exact per-type color so the panel's "ACTION"
                highlighting always matches the selected node's own card color on the canvas. */}
            <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              {/* FIX 9b (frontend handover) — editable in place of the old read-only label; staged
                  on blur via handleStageRename, never sent until the Save button above fires. */}
              <TextField
                fullWidth
                size="small"
                label="Label"
                value={renameLabel}
                onChange={(e) => setRenameLabel(e.target.value)}
                onBlur={handleStageRename}
                disabled={isStale || isSelectedNodePendingDelete}
                sx={{ mb: 1.5 }}
              />
              {/* PRISM7_CHANNEL_RULES_NODE_DESCRIPTIONS_TDD §6 — a persisted annotation now (BE
                  PATCH via handleSaveDescription), never a rule-text rewrite: deliberately NOT
                  wired to handleStageRename/pendingDiffs/serialize/confirm, and not disabled by
                  isStale — editing it must stay possible even while business-rule serialization
                  is blocked (TDD §6 point 4). */}
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                label="Description"
                value={descriptionDraft}
                onChange={(e) => {
                  setDescriptionDraft(e.target.value);
                  setDescriptionError(null);
                }}
                disabled={isSelectedNodePendingDelete || isSavingDescription}
                sx={{ mb: descriptionError ? 1 : 1.5 }}
              />
              {descriptionError && (
                <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setDescriptionError(null)}>
                  {descriptionError}
                </Alert>
              )}
              {!isDescriptionDirty && selectedNode.data.descriptionSource === 'user' && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  data-testid="inspector-custom-description-indicator"
                  sx={{ display: 'block', mt: -1, mb: 1.5, fontWeight: 700 }}
                >
                  Custom description
                </Typography>
              )}
              {isDescriptionDirty && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1.5 }}>
                  <Button size="small" onClick={handleCancelDescription} disabled={isSavingDescription}>
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => void handleSaveDescription()}
                    disabled={isSavingDescription || !descriptionDraft.trim()}
                    startIcon={isSavingDescription ? <CircularProgress size={14} /> : undefined}
                  >
                    {isSavingDescription ? 'Saving…' : 'Save'}
                  </Button>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  size="small"
                  label={selectedNode.data.nodeType}
                  sx={{
                    height: 22,
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    letterSpacing: 0.5,
                    color: RULE_NODE_COLORS[selectedNode.data.nodeType],
                    backgroundColor: `${RULE_NODE_COLORS[selectedNode.data.nodeType]}15`,
                  }}
                />
                {/* Moved here (from the flow-name header above) — this reflects THIS node's own
                    recipient-edit save in flight, not the whole flow reloading, so it belongs
                    next to the node it's actually about. */}
                {isPersisting && <CircularProgress size={14} />}
              </Box>
            </Box>
            {/* UI density fix — recommendation text now lives here instead of wrapping across
                every card on the canvas (RuleNode.tsx keeps only the blue info icon as a
                glance-able cue). Multiple entries (missing-recipient + one or more validator
                findings) each get their own line rather than a run-on sentence, same as the
                card's old rendering did. */}
            {(selectedNode.data.recommendationTexts?.length ?? 0) > 0 && (
              <Box sx={{ mb: 2 }}>
                {selectedNode.data.recommendationTexts!.map((text, i) => (
                  <Typography
                    key={i}
                    variant="body2"
                    sx={{ display: 'block', mb: 0.5, fontWeight: 500, color: '#0288d1' }}
                  >
                    {text}
                  </Typography>
                ))}
              </Box>
            )}
            {/* Recipients are structurally an ACTION-only concept — parse always emits
                recipients: [] for TRIGGER/CONDITION nodes, never a real list. Editing one on a
                non-ACTION node would call serializeRuleFlow against a node with no natural
                sentence to anchor a recipient to, producing a nonsensical LLM-fallback rewrite.
                FIX 2 (frontend handover) — an ACTION with an empty recipients array has no
                hand-off concept at all (e.g. "Review & Categorize Request"), not just an
                unresolved one; the array itself already encodes the backend's judgment on
                whether a hand-off exists, so an empty array means "don't show this control",
                not "show it with nothing in it".
                Bug found live (this fix) — gated on hasRecipientConcept (set once from the
                server's original data), NOT on the live recipients array's own length: an admin
                clearing out the last placeholder recipient to pick a real one made the live
                array empty too, and the rule above then hid the very control needed to pick a
                replacement, with auto-save persisting it that way permanently. */}
            {selectedNode.data.hasRecipientConcept && (
              <RecipientPicker
                channelId={channelId}
                value={selectedNode.data.recipients || []}
                onChange={handleRecipientsChange}
                // FIX 3 (frontend handover) — isPersisting no longer gates interaction (the
                // spinner next to the ACTION badge above is a "saving…" indicator only); isStale
                // still disables it since further edits genuinely can't be confirmed until a
                // re-parse resolves the underlying conflict. FIX 13-FE — also disabled on a node
                // staged for deletion: nothing about a node about to disappear should be editable.
                disabled={isStale || isSelectedNodePendingDelete}
              />
            )}
            {/* Convenience shortcut placed right where the edit is being made, rather than making
                the admin look back up at the pending-changes banner. Appears the moment the
                fields actually differ from this node's real, saved state — while still typing,
                before any blur has staged anything — not only once isPendingEdit is already true
                (which only happens after a blur). Clicking it stages via stageRename() and saves
                that exact result in one shot, so a live, never-blurred edit isn't silently
                skipped (see handleSaveStructuralEdits' own diffsOverride doc): this is what lets
                the admin save from here BEFORE ever triggering the banner above, which only
                appears once a blur elsewhere actually stages something — i.e. only when the admin
                moved on without using this button. Calls the same handleSaveStructuralEdits the
                banner's own Save button uses — this batches and saves EVERY pending diff across
                the whole flow, not just this node's, matching the established FIX 11 behavior
                (confirmed working as intended) rather than introducing a separate "save just this
                node" path. */}
            {hasLiveInspectorEdit && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => void handleSaveStructuralEdits(stageRename())}
                  disabled={isSavingStructural}
                  startIcon={isSavingStructural ? <CircularProgress size={14} /> : undefined}
                >
                  {isSavingStructural ? 'Saving…' : 'Save'}
                </Button>
              </Box>
            )}
          </Paper>
        )}
      </Box>

      {/* FIX 9a (frontend handover) — opened via WorkflowEdge.tsx's "Insert step here" hover
          affordance (handleOpenInsertDialog); submitting saves immediately (handleSubmitInsert's
          own doc explains why this diverges from renames, which still stage into the
          pending-changes banner). */}
      <Dialog open={insertDialogEdgeId !== null} onClose={handleCloseInsertDialog} fullWidth maxWidth="xs">
        <DialogTitle>Insert step</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {insertError && (
            <Alert severity="error" onClose={() => setInsertError(null)}>
              {insertError}
            </Alert>
          )}
          {/* A floating `label` on an outlined Select renders with a broken notch (the border
              draws straight through the label text, focused or not) on this MUI 7.3.2 +
              Chromium combination — reproduced in isolation outside this app, confirmed NOT to
              affect plain outlined TextFields (the Label/Description fields below render their
              floating labels correctly). Worked around here only, by using a static caption
              above the field instead of TextField's own `label` prop — sidesteps the buggy
              Select-specific notch path entirely without touching MUI internals or any other
              field/flow. */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Node type
            </Typography>
            <TextField
              select
              value={insertNodeType}
              onChange={(e) => setInsertNodeType(e.target.value as 'ACTION' | 'CONDITION')}
              fullWidth
              disabled={isInserting}
            >
              <MenuItem value="ACTION">Action</MenuItem>
              <MenuItem value="CONDITION">Condition</MenuItem>
            </TextField>
          </Box>
          <TextField
            label="Label"
            value={insertLabel}
            onChange={(e) => setInsertLabel(e.target.value)}
            fullWidth
            autoFocus
            disabled={isInserting}
          />
          <TextField
            label="Description"
            value={insertText}
            onChange={(e) => setInsertText(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            disabled={isInserting}
          />
          {/* Only meaningful when this step is inserted right after a CONDITION, i.e. it becomes
              one of that condition's own branches (FIX 9a). */}
          {insertDialogNeedsBranchLabel && (
            <TextField
              label="Branch label"
              value={insertBranchLabel}
              onChange={(e) => setInsertBranchLabel(e.target.value)}
              fullWidth
              disabled={isInserting}
              helperText="e.g. Yes / No — shown on the connecting line"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInsertDialog} disabled={isInserting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSubmitInsert()}
            disabled={!insertLabel.trim() || !insertText.trim() || isInserting}
            startIcon={isInserting ? <CircularProgress size={14} /> : undefined}
          >
            {isInserting ? 'Inserting…' : 'Insert'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

RuleFlowCanvas.displayName = 'RuleFlowCanvas';

export default RuleFlowCanvas;
